import clsx from 'clsx';
import { useState } from 'react';

type BlockProps = {
  /** Additional CSS classes for the block polygon */
  className?: string;
  /** SVG polygon points string */
  points: string;
  /** Fill color for the block */
  fill: string;
  /** Stroke color for the block border */
  stroke: string;
  /** Stroke width for the block border */
  strokeWidth: number;
  /** Fill opacity (0-1) */
  opacity: number;
  /** Whether the block is selected */
  isSelected?: boolean;
  /** Index for data attribute */
  index: number;
  /** Optional title/tooltip text */
  title?: string;
  /** Click handler */
  onClick?: () => void;
};

/**
 * Represents a single domain block as an SVG polygon with interactive behavior.
 *
 * @example
 * ```tsx
 * <Block
 *   points="50,150 150,150 100,50"
 *   fill="steelblue"
 *   stroke="black"
 *   strokeWidth={2}
 *   opacity={0.8}
 *   isSelected={selectedBlockId === block.id}
 *   index={blockIndex}
 *   title={block.name}
 *   onClick={() => handleBlockClick(block)}
 * />
 * ```
 */
const Block = ({
  className,
  points,
  fill,
  stroke,
  strokeWidth,
  opacity,
  isSelected = false,
  index,
  title,
  onClick,
}: BlockProps) => {
  const [isHovered, setIsHovered] = useState(false);
  // Calculate current opacity: full when selected or hovered, reduced (30%) otherwise
  const currentOpacity = isSelected || isHovered ? opacity : opacity * 0.3;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick?.();
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: <g> with role="button" for SVG accessibility
    <g
      className={clsx('group block focus:outline-0', isSelected && 'block--selected')}
      data-index={index}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !isSelected && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
    >
      <polygon
        className={clsx(
          'block-polygon cursor-pointer transition-all duration-150 ease-in-out focus:outline-0',
          className,
        )}
        points={points}
        fill={fill}
        fillOpacity={currentOpacity}
        stroke={stroke}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      >
        <title>{title}</title>
      </polygon>
    </g>
  );
};

export { Block, type BlockProps };
